import { SongList, SongListId } from '../../../common/src/model.ts';
import {
  idbClear,
  idbDelete,
  idbGet,
  idbGetAllKeys,
  idbPut,
  idbTestSetup,
  idbTestTeardown,
  SongListTimestamp,
  SongListUpdateObjectStore,
} from './idb.ts';

// - Song updates are handled via a persistent queue
// - Newly updated song lists are added to one end of the queue
// - They are removed from the other end of the queue when successfully uploaded to the network
// - The queue is stored in IndexedDB, which has an async API

let currentTimestamp: SongListTimestamp = 0; // monotonically increasing number
// settles once initializeCurrentSongUpdateTimestamp has read the queue: until then currentTimestamp is 0, below every
// key still queued from before a reload
let currentTimestampInitialized: Promise<void> = Promise.resolve();

export type DeletableSongList = SongList & { isDeleted?: boolean };

export async function initializeCurrentSongUpdateTimestamp() {
  currentTimestampInitialized = readCurrentTimestamp();
  await currentTimestampInitialized;
}

async function readCurrentTimestamp() {
  currentTimestamp = await getMaxTimestamp();
}

/**
 * Resolves once the timestamp is past every key queued before this page loaded (startup starts
 * initializeCurrentSongUpdateTimestamp without waiting for it), for a caller that must know the timestamp bounds the
 * whole queue: recordSongListUpdate, whose key must not overwrite one still queued, and the upload of a write-down mark
 * (uploadUpdatedSongLists), which would otherwise read the queue as empty and send a mark from before a reload ahead of
 * its lists. A read that failed is tried again, once per failure however many callers wait; when the retry fails too,
 * it rejects, so the caller fails (a sync pass as a transient failure, retried with the backoff) rather than going on
 * with a timestamp below the queue.
 */
export async function currentSongListUpdateTimestampInitialized(): Promise<void> {
  const initialization = currentTimestampInitialized;
  try {
    await initialization;
  } catch {
    if (currentTimestampInitialized === initialization) {
      currentTimestampInitialized = readCurrentTimestamp();
    }
    await currentTimestampInitialized;
  }
}

export async function testSetup() {
  idbTestSetup();
  await initializeCurrentSongUpdateTimestamp();
}

export async function testTeardown() {
  idbTestTeardown();
  await initializeCurrentSongUpdateTimestamp();
}

async function getMaxTimestamp() {
  const keys = await idbGetAllKeys(SongListUpdateObjectStore);
  return Math.max(...keys.map((key) => key + 1), 0);
}

export async function recordSongListUpdate(songList: DeletableSongList, { isDeleted }: { isDeleted?: boolean } = {}) {
  await currentSongListUpdateTimestampInitialized();
  const timestamp = currentTimestamp++;
  await idbPut(SongListUpdateObjectStore, { ...songList, ...(isDeleted && { isDeleted }) }, timestamp);
}

export function getCurrentSongListUpdateTimestamp(): SongListTimestamp {
  return currentTimestamp;
}

// mapId: applied to each queued list's id before lists are merged by id, so two entries queued under ids that map to
// the same list are one list (the upload passes the ids the server reassigned, issue #4190)
export async function getSongListUpdatesBefore(
  timestamp: SongListTimestamp,
  { mapId }: { mapId?: (id: SongListId) => SongListId } = {},
): Promise<DeletableSongList[]> {
  const keys = await idbGetAllKeys(SongListUpdateObjectStore);
  const songLists: DeletableSongList[] = [];
  for (const key of keys) {
    if (key >= timestamp) {
      continue;
    }
    const queued = await idbGet(SongListUpdateObjectStore, key);
    if (!queued) {
      continue;
    }
    const songList = mapId && queued.id !== undefined ? { ...queued, id: mapId(queued.id) } : queued;
    // a list recorded again keeps its first place in the queue with its latest payload: the order lists were first
    // written in can matter (a shrunk Fixed list must reach the server before the new list that follows it, issue
    // #4170), and moving the list to the end could upload a later list ahead of the one it depends on
    const index = songLists.findIndex(({ id }) => id === songList.id);
    if (index === -1) {
      songLists.push(songList);
    } else {
      songLists[index] = songList;
    }
  }
  return songLists;
}

export async function removeSongListUpdatesBefore(timestamp: SongListTimestamp) {
  const keys = await idbGetAllKeys(SongListUpdateObjectStore);
  for (const key of keys) {
    if (key >= timestamp) {
      continue;
    }
    await idbDelete(SongListUpdateObjectStore, key);
  }
}

/**
 * Rewrites each reassigned id (a key of newIds) to its new id in the queued updates recorded at or after fromTimestamp:
 * the server saved the old id's list under the new one (a temporary id, or a church's Sunday sent under a retired id,
 * issue #4190), and an update queued while that upload ran still names the old id. Updates before fromTimestamp are
 * the uploaded ones, about to be removed.
 */
export async function replaceQueuedSongListIds({
  newIds,
  fromTimestamp,
}: {
  newIds: Map<SongListId, SongListId>;
  fromTimestamp: SongListTimestamp;
}) {
  const keys = await idbGetAllKeys(SongListUpdateObjectStore);
  for (const key of keys) {
    if (key < fromTimestamp) {
      continue;
    }
    const songList = await idbGet(SongListUpdateObjectStore, key);
    const newId = songList?.id === undefined ? undefined : newIds.get(songList.id);
    if (songList && newId !== undefined) {
      await idbPut(SongListUpdateObjectStore, { ...songList, id: newId }, key);
    }
  }
}

export async function songListUpdatesPending(): Promise<boolean> {
  const keys = await idbGetAllKeys(SongListUpdateObjectStore);
  return keys.length > 0;
}

export async function clearAllSongListUpdates() {
  await idbClear(SongListUpdateObjectStore);
}
